import get from 'lodash/get';
import {
  ApiError,
  BannerFramesListType,
  CannedParametersResult,
  ContentGeneratedResult,
  FeedbackResult,
  GeneratedBannerContentDataType,
  GeneratedContentResult,
  GeneratedEmailModuleContentDataType,
  GeneratedSocialContentDataType,
  GetGeneratedContentParams,
  ImagesRecommended,
  ImageApiResponse,
  ImageData,
  TrackContentCopyResult,
  TranslatedContent,
  ContentTranslatedResult,
  GetTranslatedContentParams,
  TranslatedContentResult,
  ModuleComponentsPayload,
  SessionContextPayload,
  GeneratedContentData,
  UpdateGeneratedContentRequest,
  UpdateGeneratedContentResponse,
  GeneratedContentUpdateItem,
} from '../types/Content';
import { ContentImages } from '../types/Images';
import { EmailContentResult } from '../types/EmailModules';
import {
  FeedbackData,
  MarketingFormField,
  MarketingFormFieldRequest,
  PregenerateClaimsFeild,
  SecondaryFormApiField,
  VersionPayload,
} from '../types/MarketingBox';
import { httpClient, HTTPClientRequestPayload } from '../utils/httpClient';
import {
  PREGENERATE,
  GENERATE,
  REGENERATE,
  UPDATE_GENERATED_CONTENT,
  FEEDBACK,
  TRACK_CONTENT_COPY,
  PARTIAL_REGENERATE,
  RECOMMEND_IMAGES,
  CONTENT_ASSET,
  LOCALIZATION_TRANSLATE,
  LOCALIZATION_LANGUAGES,
  CANNED_PARAMETERES_BRANDS,
  CANNED_PARAMETERES_AUDIENCES,
  CANNED_PARAMETERES_TONES,
  CANNED_PARAMETERES_CHANNELS,
  CANNED_PARAMETERES_SECTIONS,
  CANNED_PARAMETERES_SEGMENTS,
  CANNED_PARAMETERES_PLATFORMS,
  CANNED_PARAMETERES_PRIMARY_MESSAGES,
  CONTENT_CONFIG,
  WF_TASK_DETAILS,
  CANNED_PARAMETERES_INDICATIONS,
  CANNED_PARAMETERES_COUNTRIES,
  CANNED_PARAMETERES_MODULES,
  GET_GENERATED_CONTENT,
  CANNED_PARAMETERES_ACTIONS,
  GET_EXTRACTION_DETAILS,
  GET_GENERATED_CONTENT_STATUS,
  SUPPORTING_INFORMATION,
  GENERATE_CAPTION,
  GENERATED_CONTENT_VALIDATION,
  PRIMARY_MESSAGES,
  GET_VERSION_INFO,
  GET_TRANSLATED_CONTENT_STATUS,
  GET_TRANSLATED_CONTENT,
  GET_GENERATED_CONTENT_EXPORT,
  SAVE_USER_SESSION_CONTEXT,
  GET_GENERATED_CONTENT_PREVIEW,
} from '../constants/apiRoutes';
import Logger from '../utils/logger';
import {
  ANALYTICS,
  ContentType,
  GENERATE_CONTENT,
  DestinationFormat,
  generationStatuses,
  CONVERT,
  summarizationStatuses,
  translationStatuses,
  GENERATE_CLAIMS,
  operationType as operationTypeValues,
} from '../constants/common';
import {
  getTransformedBannerContent,
  getTransformedPromptData,
  getTransformedEmailModuleData,
  getTransformedSocialData,
  getTransformedDSPModuleData,
  getTransformedBannerSuperScripts,
  getTransformedEmailModulesSuperScripts,
  getTransformedSocialSuperScripts,
  getTransformedCoreMessageData,
  getTransformedCoreMessageSuperScripts,
} from '../utils/apiResponseTransformers';
import { AvialableLanguageResult } from '../types/Language';
import {
  GeneratedContent,
  GeneratedContentDataType,
} from '../types/transformedData/MarkettingContentGeneration';
import { FeedbackResponseData } from '../types/apiResponseData/MarkettingContentGeneration';
import { FormFields, FormFieldsPayload } from '../types/PromptForm';
import {
  trackGenerateContentComplete,
  trackGenerateContentInitiate,
  trackGenerateContentSubmit,
  trackInputValues,
  trackFormComplete,
  trackSystemErrors,
  trackFormError,
} from './Analytics';
import {
  WfTaskDetailsResponseData,
  WfTaskDetailsResult,
} from '../types/WfData';
import { Claim, ClaimsResult, PrefetchedClaimsResponse } from '../types/Claims';

import {
  getEncodedUserNtid,
  getGenerateContentPayload,
  getPrefetchedClaimListIds,
  getRefinementAdditionalParams,
} from '../store/markettingContentGeneration/helpers/common';
import { marketingEnhancementDefaults } from '../context/MarkettingPromptContext/constants';
import {
  getContentGroupId,
  getContentSessionId,
  getSessionId,
} from '../utils/commonUtils';
import {
  ExtractionDetailsFuncResp,
  ExtractionDetailsResponse,
} from '../types/ExtractionDetails';
import {
  GenerateStatusResponse,
  TranslateStatusResponse,
} from '../types/GenerationStatus';
import { ApiPayloadUsed } from '../store/markettingContentGeneration/stateSegments/generateStateSegment';
import { ParamsUsed } from '../types/store/markettingContentGeneration/majorProcessActions';
import { ModuleContent } from '../types/store/markettingContentGeneration/emailModules';
import {
  DSPContentResponse,
  DSPGeneratedContent,
} from '../components/molecules/DSPRenderer/DSPRenderer.types';
import sortContentsArrayForExport from '../utils/sortContentsArrayForExport';
import { Option } from '../types/SelectInput';
import { addTranslatedLanguageLabelForSelectedModule } from '../utils/addLanguageLabelToTranslatedContent';
import { contentParamsSegment } from '../store/markettingContentGeneration/stateSegments/paramsSegment';
import store from '../store/markettingContentGeneration';
import { sequenceAndStoreClaims } from '../store/markettingContentGeneration/actions/majorProcessActions/getClaimsReferences';
import { ClaimsSequenceType } from '../store/markettingContentGeneration/stateSegments/claimsSequenceStateSegment';
import { EmailModulesSuperScriptsType } from '../types/SuperScripts';
import { APIVersionInfoResponse, VersionAPIData } from '../types/Users';
import {
  ApplicableRecommendation,
  ValiditionApiResponse,
} from '../types/contentValidation';
import {
  SaveSessionContextApiResponse,
  RequestParams,
} from '../types/userSessionContext';
import { getParamsUsed } from '../utils/requestParamsUtils';

const handleError = (): GeneratedContent => {
  const errorResponse = {
    success: false,
  };
  return errorResponse;
};

// Type guard to check if data contains generatedContent
const isGeneratedContent = (
  data: ContentGeneratedResult['data'] | ContentTranslatedResult['data']
): data is ContentGeneratedResult['data'] => 'generatedContent' in data;

const getScore = (
  data: ContentGeneratedResult['data'] | ContentTranslatedResult['data']
): number => {
  if (isGeneratedContent(data)) {
    const generatedContent = data.generatedContent as
      | GeneratedBannerContentDataType[]
      | GeneratedSocialContentDataType[];
    return generatedContent[0]?.score?.value;
  }
  const translatedContent = data.translatedContent as
    | GeneratedBannerContentDataType[]
    | GeneratedSocialContentDataType[];
  return translatedContent[0]?.score?.value;
};

const getScoreForEmailModules = (
  content: GeneratedEmailModuleContentDataType[]
): number => {
  return content?.[0]?.score?.value;
};

const getContentId = (
  data: ContentGeneratedResult['data'] | ContentTranslatedResult['data']
): string => {
  if (isGeneratedContent(data)) {
    const generatedContent = data.generatedContent as
      | GeneratedBannerContentDataType[]
      | GeneratedSocialContentDataType[];
    return generatedContent[0].id;
  }

  const translatedContent = data.translatedContent as
    | GeneratedBannerContentDataType[]
    | GeneratedSocialContentDataType[];
  return translatedContent[0].id;
};

const getRequestParams = (
  data: ContentGeneratedResult['data'] | ContentTranslatedResult['data']
): unknown => {
  if (isGeneratedContent(data)) {
    const generatedContent = data.generatedContent as
      | GeneratedBannerContentDataType[]
      | GeneratedSocialContentDataType[];
    return (
      (generatedContent[0] as unknown as { requestParams?: unknown })
        ?.requestParams || null
    );
  }

  const translatedContent = data.translatedContent as
    | GeneratedBannerContentDataType[]
    | GeneratedSocialContentDataType[];
  return (
    (translatedContent[0] as unknown as { requestParams?: unknown })
      ?.requestParams || null
  );
};

const getContentIdForEmailModules = (
  content: GeneratedEmailModuleContentDataType[]
): string => {
  return content?.[0].id || '';
};

const extractGeneratedContentFirstVersion = (
  data: GeneratedEmailModuleContentDataType[] | DSPGeneratedContent[]
) => data?.[0];

export const getGeneratedContentFormatted = (
  contentType: string,
  data: ContentGeneratedResult['data'] | ContentTranslatedResult['data'],
  destinationFormat?: string | undefined | null,
  claimSequences: ClaimsSequenceType = {}
): GeneratedContent => {
  const contentKey =
    'generatedContent' in data ? 'generatedContent' : 'translatedContent';

  // Get request params once for all cases that need it
  const requestParams = getRequestParams(data);

  // Extract language information from the content array
  const contentArray = get(data, contentKey, []);

  // For multi-language responses (translated content), return the first language's data
  // but include all languages in the content structure for export functionality
  const getLanguageFromContent = () => {
    if (contentArray.length > 0) {
      const firstItem = contentArray[0] as Record<string, unknown>;
      // Different content types store language information differently
      if (
        firstItem &&
        'langCode' in firstItem &&
        typeof firstItem.langCode === 'string'
      ) {
        return firstItem.langCode; // For email modules
      }
      if (
        firstItem &&
        'language' in firstItem &&
        typeof firstItem.language === 'string'
      ) {
        return firstItem.language; // For claims and other types
      }
    }
    return undefined;
  };

  const extractedLanguage = getLanguageFromContent();

  switch (contentType) {
    case ContentType.BANNER:
      return {
        success: true,
        nonSourceLangDetected:
          !data.isInclusionsExclusionsInContentLang || false,
        language: extractedLanguage,
        data: {
          id: getContentId(data),
          apiPayloadUsed: requestParams,
          paramsUsed: requestParams
            ? getParamsUsed(requestParams as RequestParams, false)
            : null,
          score: {
            value: getScore(data),
          },

          markdownText: '',
          content: getTransformedBannerContent(
            get(data, contentKey, []) as GeneratedBannerContentDataType[]
          ),
          superScripts: getTransformedBannerSuperScripts(
            get(data, contentKey, []) as GeneratedBannerContentDataType[],
            claimSequences
          ),
        },
      };

    case ContentType.RTE:
    case ContentType.RTE.toUpperCase():
    case ContentType.EMAIL:
      return {
        success: true,
        nonSourceLangDetected:
          !data.isInclusionsExclusionsInContentLang || false,
        data: {
          id: getContentIdForEmailModules(
            get(data, contentKey, []) as GeneratedEmailModuleContentDataType[]
          ),
          apiPayloadUsed: requestParams,
          paramsUsed: requestParams
            ? getParamsUsed(requestParams as RequestParams, false)
            : null,
          score: {
            value: getScoreForEmailModules(
              get(data, contentKey, []) as GeneratedEmailModuleContentDataType[]
            ),
          },
          markdownText: '',
          content: extractGeneratedContentFirstVersion(
            getTransformedEmailModuleData(
              get(data, contentKey, []) as GeneratedEmailModuleContentDataType[]
            ) as GeneratedEmailModuleContentDataType[]
          ),
          superScripts: getTransformedEmailModulesSuperScripts(
            get(
              data,
              `${contentKey}[0].superScripts`,
              []
            ) as EmailModulesSuperScriptsType[],
            claimSequences
          ),
        },
      };

    case ContentType.UPLOAD:
      if (
        destinationFormat?.toLowerCase() ===
        DestinationFormat.BANNER.toLowerCase()
      ) {
        return {
          success: true,
          nonSourceLangDetected:
            !data.isInclusionsExclusionsInContentLang || false,
          data: {
            id: getContentId(data),
            apiPayloadUsed: requestParams,
            paramsUsed: requestParams
              ? getParamsUsed(requestParams as RequestParams, false)
              : null,
            score: {
              value: getScore(data),
            },
            markdownText: '',
            content: getTransformedBannerContent(
              get(data, contentKey, []) as BannerFramesListType[]
            ),
          },
        };
      }

      if (
        destinationFormat?.toLowerCase() ===
        DestinationFormat.SOCIAL.toLowerCase()
      ) {
        return {
          success: true,
          nonSourceLangDetected:
            !data.isInclusionsExclusionsInContentLang || false,
          data: {
            id: getContentId(data),
            apiPayloadUsed: requestParams,
            paramsUsed: requestParams
              ? getParamsUsed(requestParams as RequestParams, false)
              : null,
            score: {
              value: getScore(data),
            },
            markdownText: '',
            content: getTransformedSocialData(get(data, contentKey, [])),
          },
        };
      }

      if (
        destinationFormat?.toLowerCase() ===
        DestinationFormat.CORE_MESSAGE.toLowerCase()
      ) {
        return {
          success: true,
          nonSourceLangDetected:
            !data.isInclusionsExclusionsInContentLang || false,
          data: {
            id: getContentId(data),
            apiPayloadUsed: requestParams,
            paramsUsed: requestParams
              ? getParamsUsed(requestParams as RequestParams, false)
              : null,
            score: {
              value: getScore(data),
            },
            markdownText: '',
            content: getTransformedCoreMessageData(get(data, contentKey, [])),
            superScripts: getTransformedCoreMessageSuperScripts(
              get(data, contentKey, []),
              claimSequences
            ),
          },
        };
      }

      if (
        destinationFormat?.toLowerCase() ===
          DestinationFormat.EMAIL.toLowerCase() ||
        destinationFormat?.toLowerCase() === DestinationFormat.RTE.toLowerCase()
      ) {
        return {
          success: true,
          nonSourceLangDetected:
            !data.isInclusionsExclusionsInContentLang || false,
          data: {
            id: getContentIdForEmailModules(
              get(data, contentKey, []) as GeneratedEmailModuleContentDataType[]
            ),
            apiPayloadUsed: requestParams,
            paramsUsed: requestParams
              ? getParamsUsed(requestParams as RequestParams, false)
              : null,
            score: {
              value: getScoreForEmailModules(
                get(
                  data,
                  contentKey,
                  []
                ) as GeneratedEmailModuleContentDataType[]
              ),
            },
            markdownText: '',
            content: extractGeneratedContentFirstVersion(
              getTransformedEmailModuleData(
                get(
                  data,
                  contentKey,
                  []
                ) as GeneratedEmailModuleContentDataType[]
              ) as GeneratedEmailModuleContentDataType[]
            ),
            superScripts: [],
          },
        };
      }

      return {
        success: true,
        nonSourceLangDetected:
          !data.isInclusionsExclusionsInContentLang || false,
        data: {
          id: getContentId(data),
          apiPayloadUsed: requestParams,
          paramsUsed: requestParams
            ? getParamsUsed(requestParams as RequestParams, false)
            : null,
          score: {
            value: getScore(data),
          },
          markdownText: '',
          content: extractGeneratedContentFirstVersion(
            getTransformedDSPModuleData(
              get(data, contentKey, []) as DSPGeneratedContent[]
            )
          ),
        },
      };

    case ContentType.SOCIAL:
    case 'social':
      return {
        success: true,
        nonSourceLangDetected:
          !data.isInclusionsExclusionsInContentLang || false,
        data: {
          id: getContentId(data),
          apiPayloadUsed: requestParams,
          paramsUsed: requestParams
            ? getParamsUsed(requestParams as RequestParams, false)
            : null,
          score: {
            value: getScore(data),
          },
          markdownText: '',
          content: getTransformedSocialData(get(data, contentKey, [])),
          superScripts: getTransformedSocialSuperScripts(
            get(data, contentKey, []) as GeneratedSocialContentDataType[],
            claimSequences
          ),
        },
      };

    case 'CoreMessage':
    case 'coremessage':
      return {
        success: true,
        nonSourceLangDetected:
          !data.isInclusionsExclusionsInContentLang || false,
        data: {
          id: getContentId(data),
          apiPayloadUsed: requestParams,
          paramsUsed: requestParams
            ? getParamsUsed(requestParams as RequestParams, false)
            : null,
          score: {
            value: getScore(data),
          },
          markdownText: '',
          content: getTransformedCoreMessageData(get(data, contentKey, [])),
          superScripts: getTransformedCoreMessageSuperScripts(
            get(data, contentKey, []),
            claimSequences
          ),
        },
      };

    default:
      return {
        success: true,
        nonSourceLangDetected:
          !data.isInclusionsExclusionsInContentLang || false,
        data: {
          id: getContentId(data),
          apiPayloadUsed: requestParams,
          paramsUsed: requestParams
            ? getParamsUsed(requestParams as RequestParams, false)
            : null,
          markdownText: JSON.stringify(data),
          score: {
            value: getScore(data),
          },
        },
      };
  }
};

export const fetchPreGeneratedContent = async (
  marketingFormField: MarketingFormFieldRequest
): Promise<ClaimsResult> => {
  const {
    action,
    brand,
    indication,
    country,
    targetAudience,
    segment,
    contentType,
    tone,
    topics,
    language,
    platform,
    emailModules,
    fileName,
    sessionId,
    destinationFormat,
    extendedLibraryEnabled,
  } = marketingFormField;
  const payload: HTTPClientRequestPayload = {
    action,
    brand,
    indication,
    country,
    targetAudience,
    persona: segment,
    channel:
      (contentType === ContentType.UPLOAD && destinationFormat) || contentType,
    tone,
    language,
    topic: topics,
    platform,
    fileName,
    emailModules: emailModules?.split(','),
    extendedLibraryEnabled: extendedLibraryEnabled || false,
  };

  if (sessionId) {
    payload.sessionId = sessionId;
  }
  try {
    const response = await httpClient.post<PrefetchedClaimsResponse>(
      PREGENERATE,
      payload
    );
    const { data, success } = response as PrefetchedClaimsResponse;
    if (success && data) {
      return { success, data };
    }
    return {
      success: false,
    };
  } catch (error) {
    const postError = error as ApiError;
    Logger.error(new Error(postError.message));
    return {
      success: false,
    };
  }
};

export const generateStatus = async (
  marketingFormField: ApiPayloadUsed,
  contentId: string
): Promise<GenerateStatusResponse> => {
  const { action, sessionId, contentGroupId } = marketingFormField;
  try {
    const response = await httpClient.get<GenerateStatusResponse>(
      GET_GENERATED_CONTENT_STATUS(
        sessionId as string,
        action as string,
        contentId as string,
        contentGroupId || getContentGroupId()
      )
    );
    const { data, success } = response as GenerateStatusResponse;
    if (success && data) {
      return { data, success };
    }
    return handleError() as GenerateStatusResponse;
  } catch (error) {
    const postError = error as ApiError;
    Logger.error(new Error(postError.message));
    return handleError() as GenerateStatusResponse;
  }
};

export const getGeneratedContent = async ({
  action,
  sessionId,
  contentSessionId = getContentSessionId(),
  contentId,
  contentGroupId,
  channel = '',
  page = 1,
  pageSize = 20,
  sort = '',
}: GetGeneratedContentParams): Promise<GeneratedContentResult> => {
  try {
    const response = await httpClient.get<EmailContentResult>(
      GET_GENERATED_CONTENT(
        sessionId,
        contentSessionId,
        action,
        contentId,
        contentGroupId || getContentGroupId(),
        channel,
        page,
        pageSize,
        sort
      )
    );
    const { data, success } = response as GeneratedContentResult;
    if (success && data) {
      return {
        success,
        data,
      };
    }
    return handleError() as GeneratedContentResult;
  } catch (error) {
    const postError = error as ApiError;
    Logger.error(new Error(postError.message));
    return handleError() as GeneratedContentResult;
  }
};

export const getTranslatedContent = async ({
  sessionId,
  action,
  contentId,
  contentGroupId,
  channel = '',
  page,
  pageSize,
  languages,
  language,
  sort = '',
  allTranslations,
}: GetTranslatedContentParams): Promise<TranslatedContentResult> => {
  try {
    const response = await httpClient.get<EmailContentResult>(
      GET_TRANSLATED_CONTENT(
        sessionId,
        action,
        contentId,
        languages,
        contentGroupId || getContentGroupId(),
        channel,
        page,
        pageSize,
        sort,
        allTranslations,
        language
      )
    );
    const { data, success } = response as TranslatedContentResult;
    if (success && data) {
      return {
        success,
        data,
      };
    }
    return handleError() as TranslatedContentResult;
  } catch (error) {
    const postError = error as ApiError;
    Logger.error(new Error(postError.message));
    return handleError() as TranslatedContentResult;
  }
};

export const getTranslatedContentForExport = async ({
  sessionId,
  action,
  contentId,
  contentGroupId,
  channel = '',
  page,
  pageSize,
  languages,
  language,
  sort = '',
  allTranslations,
}: GetTranslatedContentParams): Promise<GeneratedContent> => {
  try {
    const translatedContentResult = await getTranslatedContent({
      sessionId,
      action,
      contentId,
      contentGroupId,
      channel,
      page,
      pageSize,
      languages,
      language,
      sort,
      allTranslations,
    });

    if (translatedContentResult.success && translatedContentResult.data) {
      // Create a compatible data structure for sequenceAndStoreClaims and getGeneratedContentFormatted
      const compatibleData = {
        sessionId: translatedContentResult.data.sessionId,
        action: translatedContentResult.data.action,
        channel: translatedContentResult.data.channel,
        contentGroupId: translatedContentResult.data.contentGroupId,
        contentType: channel as ContentType,
        status: 'completed',
        isInclusionsExclusionsInContentLang: true, // Default to true for translated content
        translatedContent: translatedContentResult.data
          .translatedContent as unknown as GeneratedContentData[], // Type casting for compatibility
      };

      const claimSequences = sequenceAndStoreClaims(compatibleData, true);

      return getGeneratedContentFormatted(
        channel,
        compatibleData,
        undefined,
        claimSequences
      );
    }

    return handleError();
  } catch (error) {
    const postError = error as ApiError;
    Logger.error(new Error(postError.message));
    return handleError();
  }
};

export async function generatedContentPoller({
  sessionId,
  contentGroupId,
  action,
  contentId,
  channel,
}: {
  sessionId: string;
  contentGroupId: string;
  action: string;
  contentId: string;
  channel?: string;
}): Promise<
  | GeneratedContentResult
  | DSPContentResponse
  | { success: boolean; data: unknown; nonSourceLangDetected?: boolean }
> {
  return new Promise(resolve => {
    const payload = {
      sessionId: sessionId || '',
      action: action || '',
    };

    const interval = setInterval(async () => {
      const {
        success: generateStatusSuccess,
        data: { generatedContentStatus = {} } = {},
      } = await generateStatus(payload as ApiPayloadUsed, contentId);

      if (generateStatusSuccess) {
        const { status } =
          (Array.isArray(generatedContentStatus) &&
            generatedContentStatus[0]) ||
          {};
        if (
          status === generationStatuses.GENERATION_COMPLETED ||
          status === summarizationStatuses.SUMMARIZATION_COMPLETED
        ) {
          const requestParams: GetGeneratedContentParams = {
            sessionId,
            contentGroupId,
            action,
            contentId,
            contentSessionId: getContentSessionId(),
            // page,
            // pageSize
          };

          if (import.meta.env.MODE === 'development') {
            requestParams.channel = channel;
          }
          clearInterval(interval);
          const response = await getGeneratedContent(requestParams);
          resolve(response);
        } else if (
          status === generationStatuses.GENERATION_ERROR ||
          status === summarizationStatuses.SUMMARIZATION_COMPLETED
        ) {
          clearInterval(interval);
          resolve({ success: false });
        }
      } else {
        clearInterval(interval);
        resolve({ success: false });
      }
    }, 5000);
  });
}

export const fetchGeneratedContent = async (
  marketingFormField: MarketingFormFieldRequest & PregenerateClaimsFeild
): Promise<GeneratedContent> => {
  const {
    brand,
    indication,
    country,
    targetAudience,
    segment,
    sections,
    contentType,
    tone,
    topics,
    platform,
    useSubstituteClaims,
    claims,
    emailModules,
    action,
    destinationFormat,
    fileName,
    language,
    sessionId,
    contentGroupId,
    unbranded,
    context,
    quotes,
    extendedLibraryEnabled,
  } = marketingFormField;
  const payload: HTTPClientRequestPayload = {
    brand,
    indication,
    country,
    targetAudience,
    persona: segment,
    sections,
    channel:
      (contentType === ContentType.UPLOAD && destinationFormat) || contentType,
    tone,
    topic: topics,
    platform,
    useSubstituteClaims,
    claims,
    emailModules: emailModules?.split(','),
    action,
    sessionId: sessionId || getSessionId(),
    contentSessionId: getContentSessionId(),
    contentGroupId: contentGroupId || getContentGroupId(),
    fileName,
    language,
    unbranded,
    operationType: operationTypeValues.GENERATE,
    context,
    quotes: quotes || [],
    extendedLibraryEnabled: extendedLibraryEnabled || false,
  };

  // Tracking analytics for Generate API initiation
  trackGenerateContentInitiate(ANALYTICS.GENERATE_FORM_API_NAME);
  try {
    const generateResponse = await httpClient.post<ContentGeneratedResult>(
      GENERATE,
      payload
    );

    if (!(generateResponse as ContentGeneratedResult).success) {
      return handleError();
    }
    const emailContentGroupId =
      contentType === ContentType.EMAIL ? payload.contentGroupId : '';

    const response = await generatedContentPoller({
      action: payload.action,
      contentGroupId: emailContentGroupId,
      contentId: get(generateResponse, 'data.generatedContent[0].id', ''),
      sessionId: get(generateResponse, 'data.sessionId', ''),
      channel: payload.channel,
    });

    const { data, success } = response as unknown as ContentGeneratedResult;

    if (success && data) {
      // Tracking analytics for Generate API completion
      // if content type is dsp don't fire this event becuase dsp will poll after this api and
      // after successfull poll it will ask for the generated content
      trackGenerateContentComplete(ANALYTICS.GENERATE_FORM_API_NAME);

      const claimSequences = sequenceAndStoreClaims(data);
      return getGeneratedContentFormatted(
        contentType,
        data,
        destinationFormat,
        claimSequences
      );
    }

    // Tracking analytics for system errors from generate API
    trackSystemErrors(ANALYTICS.GENERATE_FORM_API_NAME);
    return handleError();
  } catch (error) {
    const postError = error as ApiError;
    Logger.error(new Error(postError.message));
    // Tracking analytics for system errors from generate API
    trackSystemErrors(ANALYTICS.GENERATE_FORM_API_NAME);
    return handleError();
  }
};

const getExclusionValue = (exclusions: string[]): string[] => {
  if (!exclusions || exclusions.length === 0) {
    return [];
  }

  return exclusions.map(str => str.trim());
};

export const fetchRegeneratedContent = async (
  marketingFormField: MarketingFormFieldRequest &
    SecondaryFormApiField &
    VersionPayload & {
      moduleData?: ModuleComponentsPayload;
      generateNewContentVersion?: boolean;
    } & {
      applyRecommendations?: ApplicableRecommendation[];
    }
): Promise<GeneratedContent> => {
  const {
    brand,
    indication,
    country,
    targetAudience,
    segment,
    sections,
    contentType,
    tone,
    toneLevel,
    platform,
    topics,
    exclusions,
    inclusions,
    verbalIdentity = [],
    versionIds = [],
    parentId,
    emailModules,
    destinationFormat,
    sessionId,
    contentGroupId,
    action,
    fileName,
    language,
    operationType,
    unbranded,
    context,
    moduleData,
    generateNewContentVersion,
    contentId,
    applyRecommendations,
    contentStyle,
    extendedLibraryEnabled,
    quotes,
  } = marketingFormField;

  const payload: HTTPClientRequestPayload = {
    parentId,
    brand,
    indication,
    country,
    targetAudience,
    persona: segment,
    sections,
    contentSessionId: getContentSessionId(),
    channel:
      (contentType === ContentType.UPLOAD && destinationFormat) || contentType,
    tone,
    platform,
    topic: topics,
    versionIds,
    emailModules: emailModules?.split(','),
    inclusions,
    action,
    sessionId: contentType === ContentType.UPLOAD ? sessionId : getSessionId(),
    contentGroupId: contentGroupId || getContentGroupId(),
    fileName,
    language,
    operationType,
    unbranded,
    verbalIdentity,
    context,
    generateNewContentVersion,
    contentId,
    contentStyle,
    toneLevel,
    extendedLibraryEnabled,
    quotes,
  };

  if (exclusions) {
    const exclusionsValue = getExclusionValue(exclusions || []);
    payload.exclusions = exclusionsValue;
  } else {
    payload.exclusions = [];
  }

  if (moduleData) {
    payload.operationType = operationTypeValues.REGENERATE;
    payload.moduleData = moduleData;
  }

  if (applyRecommendations && applyRecommendations.length > 0) {
    payload.applyRecommendations = applyRecommendations;
  }

  // Tracking analytics for Regenerate API initiation
  trackGenerateContentInitiate(ANALYTICS.REGENERATE_FORM_API_NAME);
  try {
    const generateResponse = await httpClient.post<ContentGeneratedResult>(
      REGENERATE,
      payload
    );

    if (!(generateResponse as ContentGeneratedResult).success) {
      return handleError();
    }
    const emailContentGroupId =
      contentType === ContentType.EMAIL ? payload.contentGroupId : '';

    const response = await generatedContentPoller({
      action: payload.action,
      contentGroupId: emailContentGroupId,
      contentId: get(generateResponse, 'data.generatedContent[0].id', ''),
      sessionId: get(generateResponse, 'data.sessionId', ''),
      channel: payload.channel,
    });

    const { success, data } = response as unknown as ContentGeneratedResult;
    if (success && data) {
      // Tracking analytics for Regenerate API completion
      // if contnent type is dsp don't fire this event becuase dsp will poll after this api and
      // after successfull poll it will ask for the generated content
      trackGenerateContentComplete(ANALYTICS.REGENERATE_FORM_API_NAME);

      const claimSequences = sequenceAndStoreClaims(data);
      return getGeneratedContentFormatted(
        contentType,
        data,
        destinationFormat,
        claimSequences
      );
    }
    // Tracking analytics for system errors from regenerate API
    trackSystemErrors(ANALYTICS.REGENERATE_FORM_API_NAME);
    return handleError();
  } catch (error) {
    const postError = error as ApiError;
    Logger.error(new Error(postError.message));
    // Tracking analytics for system errors from regenerate API
    trackSystemErrors(ANALYTICS.REGENERATE_FORM_API_NAME);
    return handleError();
  }
};

export const loadPromptFieldOptions = async (
  formField: FormFields,
  payload: FormFieldsPayload
) => {
  const queryParams = () =>
    Object.entries(payload)
      .filter(([, value]) => value !== undefined)
      .map(
        ([key, value]) =>
          `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`
      )
      .join('&');

  const cannedParamsUrls: Record<string, string> = {
    [FormFields.BRAND]: CANNED_PARAMETERES_BRANDS,
    [FormFields.INDICATION]: CANNED_PARAMETERES_INDICATIONS,
    [FormFields.COUNTRY]: CANNED_PARAMETERES_COUNTRIES,
    [FormFields.AUDIENCE]: CANNED_PARAMETERES_AUDIENCES,
    [FormFields.TONE]: CANNED_PARAMETERES_TONES,
    [FormFields.PRIMARYMESSAGE]: CANNED_PARAMETERES_PRIMARY_MESSAGES,
    [FormFields.CHANNEL]: CANNED_PARAMETERES_CHANNELS,
    [FormFields.SECTION]: CANNED_PARAMETERES_SECTIONS,
    [FormFields.SEGMENT]: CANNED_PARAMETERES_SEGMENTS,
    [FormFields.PLATFORM]: CANNED_PARAMETERES_PLATFORMS,
    [FormFields.MODULE]: CANNED_PARAMETERES_MODULES,
    [FormFields.ACTION]: CANNED_PARAMETERES_ACTIONS,
    [FormFields.DESTINATION_FORMAT]: CANNED_PARAMETERES_CHANNELS,
    [FormFields.LANGUAGE]: LOCALIZATION_LANGUAGES,
    [FormFields.PRIMARY_MESSAGES]: PRIMARY_MESSAGES,
  };

  const url = `${cannedParamsUrls[formField]}?${queryParams()}`;

  try {
    const response = await httpClient.get<CannedParametersResult>(url);

    const { data, success, message } = response as CannedParametersResult;
    return { success, message, data };
  } catch (error) {
    const fetchError = error as ApiError;
    Logger.error(new Error(fetchError.message));
    return handleError();
  }
};

export const updateFeedback = async (
  feedbackPayload: FeedbackData
): Promise<FeedbackResponseData> => {
  const payload: HTTPClientRequestPayload = {
    ...feedbackPayload,
  };

  try {
    const response = await httpClient.post<FeedbackResult>(FEEDBACK, payload);
    const { data, success } = response as FeedbackResult;

    if (success && data) {
      return { success };
    }
    return { success: false };
  } catch (error) {
    const postError = error as ApiError;
    Logger.error(new Error(postError.message));
    return { success: false };
  }
};

export const trackContentCopy = async (
  id: string,
  parentId: string,
  sessionId: string
) => {
  const payload: HTTPClientRequestPayload = {
    id,
    parentId,
    sessionId,
  };

  try {
    const response = await httpClient.post<TrackContentCopyResult>(
      TRACK_CONTENT_COPY,
      payload
    );
    const { success } = response as TrackContentCopyResult;

    if (success) {
      return { data: {}, success };
    }
    return { success: false };
  } catch (error) {
    const postError = error as ApiError;
    Logger.error(new Error(postError.message));
    return { success: false };
  }
};

export const fetchAvailableLanguage = async (selectedCountry: string) => {
  try {
    const response = await httpClient.get<AvialableLanguageResult>(
      `${LOCALIZATION_LANGUAGES}?countryName=${selectedCountry}`
    );
    const { data, success, message } = response as AvialableLanguageResult;
    return { success, message, data };
  } catch (error) {
    const fetchError = error as ApiError;
    Logger.error(new Error(fetchError.message));
    return handleError();
  }
};

const getFormattedDestinationFormat = (destinationFormat?: string) => {
  return destinationFormat === ContentType.SOCIAL
    ? DestinationFormat.SOCIAL
    : destinationFormat;
};

export const translateStatus = async (
  marketingFormField: ApiPayloadUsed,
  contentId: string
): Promise<TranslateStatusResponse> => {
  const { action, sessionId, contentGroupId, language } = marketingFormField;
  try {
    const response = await httpClient.get<TranslateStatusResponse>(
      GET_TRANSLATED_CONTENT_STATUS(
        sessionId as string,
        action as string,
        contentId as string,
        language as string,
        contentGroupId as string
      )
    );
    const { data, success } = response as TranslateStatusResponse;
    if (success && data) {
      return { data, success };
    }
    return handleError() as TranslateStatusResponse;
  } catch (error) {
    const postError = error as ApiError;
    Logger.error(new Error(postError.message));
    return handleError() as TranslateStatusResponse;
  }
};

export async function translatedContentPoller({
  sessionId,
  action,
  contentId,
  contentGroupId,
  channel,
  page,
  pageSize,
  language,
}: {
  sessionId: string;
  action: string;
  contentId: string;
  language: string;
  contentGroupId?: string;
  channel?: string;
  page?: number;
  pageSize?: number;
}): Promise<
  | TranslatedContentResult
  | DSPContentResponse
  | { success: boolean; data: unknown; nonSourceLangDetected?: boolean }
> {
  return new Promise(resolve => {
    const payload = {
      language,
      contentGroupId: contentGroupId || getContentGroupId(),
      contentId: contentId || '',
      sessionId: sessionId || '',
      action: action || '',
    };

    const interval = setInterval(async () => {
      const {
        success: translateStatusSuccess,
        data: { translatedContentStatus = {} } = {},
      } = await translateStatus(payload as ApiPayloadUsed, contentId);

      if (translateStatusSuccess) {
        const { status } =
          (Array.isArray(translatedContentStatus) &&
            translatedContentStatus[0]) ||
          {};
        if (status === translationStatuses.TRANSLATION_COMPLETED) {
          const requestParams: GetTranslatedContentParams = {
            sessionId,
            action,
            contentId,
            contentGroupId,
            channel,
            page,
            pageSize,
            language,
          };

          if (import.meta.env.MODE === 'development') {
            requestParams.channel = channel;
          }
          clearInterval(interval);
          const response = await getTranslatedContent(requestParams);
          resolve(response);
        } else if (status === translationStatuses.TRANSLATION_ERROR) {
          clearInterval(interval);
          resolve({ success: false });
        }
      } else {
        clearInterval(interval);
        resolve({ success: false });
      }
    }, 5000);
  });
}

export const fetchTranslatedContent = async ({
  contentId,
  languageId,
  contentType,
  action,
  optionId,
  sections,
  destinationFormat,
  page,
  pageSize,
}: {
  contentId: string;
  languageId: string;
  contentType: string;
  action?: string;
  optionId?: string;
  sections?: string[];
  destinationFormat?: string;
  page?: number;
  pageSize?: number;
}): Promise<GeneratedContent> => {
  const formattedDestinationFormat =
    destinationFormat?.toUpperCase() === ContentType.RTE.toUpperCase()
      ? destinationFormat
      : getFormattedDestinationFormat(destinationFormat?.toLowerCase());

  const getChannel = () => {
    switch (contentType?.toLowerCase()) {
      case ContentType.RTE.toLowerCase():
        return contentType?.toUpperCase();

      case ContentType.UPLOAD.toLowerCase():
        return formattedDestinationFormat || contentType;

      default:
        return contentType;
    }
  };

  const payload: HTTPClientRequestPayload = {
    id: contentId,
    sessionId: getSessionId(),
    languageId,
    channel: getChannel(),
    action,
  };

  if (optionId) {
    const translateOptions = [
      {
        optionId,
        ...(Array.isArray(sections) &&
          sections.length > 0 && { fields: [...sections] }),
      },
    ];

    payload.translateOptions = translateOptions;
  }

  try {
    const generateResponse = await httpClient.post<ContentTranslatedResult>(
      LOCALIZATION_TRANSLATE,
      payload
    );

    if (!(generateResponse as ContentTranslatedResult).success) {
      return handleError();
    }

    let response = generateResponse;

    if (action === GENERATE_CLAIMS) {
      response = await translatedContentPoller({
        sessionId: get(generateResponse, 'data.sessionId', ''),
        action: payload.action,
        contentId: `${contentId}`,
        contentGroupId: payload.contentGroupId ?? '',
        channel: payload.channel ?? '',
        page,
        pageSize,
        language: languageId,
      });
    }

    const { data, success } = response as ContentTranslatedResult;

    if (success && data) {
      const claimSequences = sequenceAndStoreClaims(data, true);

      return getGeneratedContentFormatted(
        contentType,
        data,
        formattedDestinationFormat,
        claimSequences
      );
    }

    return handleError();
  } catch (error) {
    const postError = error as ApiError;
    Logger.error(new Error(postError.message));
    return handleError();
  }
};

export interface RegenerateSectionDetails {
  optionId: string;
  sections?: string[];
  imageIds?: string[];
}

export const fetchPartialRegeneratedContent = async (
  marketingFormField: MarketingFormField &
    SecondaryFormApiField &
    VersionPayload & {
      contentId: string;
      verbalIdentity?: Array<{
        name: string;
        value: boolean;
      }>;
    },
  regenerateSectionDetails: RegenerateSectionDetails
): Promise<GeneratedContent> => {
  const {
    brand,
    action,
    indication,
    country,
    targetAudience,
    segment,
    sections,
    contentType,
    tone,
    toneLevel,
    topics,
    exclusions,
    inclusions,
    contentStyle,
    versionIds = [],
    parentId,
    contentId,
    language,
    emailModules,
    destinationFormat,
    unbranded,
    context,
    verbalIdentity = [],
    channel,
  } = marketingFormField;

  // Create a ParamsUsed object from the marketingFormField for getRefinementAdditionalParams
  const paramsUsedForRefinement: Partial<ParamsUsed> = {
    currentSelectedContentStyle: contentStyle || '',
    currentSelectedTone: tone || '',
    currentSelectedToneLevel: toneLevel || '',
    currentContextValue: context?.text || '',
    currentSelectedExclusion: exclusions || [],
    currentSelectedInclusion: inclusions || [],
    currentSelectedDaring: verbalIdentity?.[0]?.value || false,
    currentSelectedCompassionate: verbalIdentity?.[1]?.value || false,
    currentAvailableContentStyleOptions:
      marketingEnhancementDefaults.currentAvailableContentStyleOptions || [],
  };

  const refinePayload = getRefinementAdditionalParams(
    paramsUsedForRefinement as ParamsUsed
  );

  const contentTypeValue = contentType || channel || '';

  const payload: HTTPClientRequestPayload = {
    action,
    parentId,
    brand,
    indication,
    country,
    targetAudience,
    persona: segment,
    sections,
    channel:
      (contentTypeValue === ContentType.UPLOAD && destinationFormat) ||
      contentTypeValue,
    contentSessionId: getContentSessionId(),
    tone,
    topic: topics,
    versionIds,
    contentId,
    language,
    emailModules: (() => {
      if (emailModules) {
        if (Array.isArray(emailModules)) {
          return emailModules;
        }
        return emailModules.split(',');
      }
      return [];
    })(),
    inclusions,
    sessionId: getSessionId(),
    contentGroupId: getContentGroupId(),
    unbranded,
    context,
    operationType: operationTypeValues.PARTIAL_REGENERATE,
    ...refinePayload,
  };

  payload.regenerateOptions = [
    {
      optionId: regenerateSectionDetails.optionId,
      fields: regenerateSectionDetails.sections,
    },
  ];

  const contextValue = get(payload, 'context.text', '');

  if (!contextValue || contextValue?.trim() === '') {
    delete payload.context;
  }

  try {
    const generateResponse = await httpClient.post<ContentGeneratedResult>(
      PARTIAL_REGENERATE,
      payload
    );

    if (!(generateResponse as ContentGeneratedResult).success) {
      return handleError();
    }

    const contentGroupId =
      (emailModules || []).length > 0 ? payload.contentGroupId : '';

    const response = await generatedContentPoller({
      action: payload.action,
      contentGroupId,
      contentId: get(generateResponse, 'data.generatedContent[0].id', ''),
      sessionId: get(generateResponse, 'data.sessionId', ''),
      channel: payload.channel,
    });

    const { data, success } = response as unknown as ContentGeneratedResult;

    if (success && data) {
      const claimSequences = sequenceAndStoreClaims(data);
      return getGeneratedContentFormatted(
        contentTypeValue,
        data,
        destinationFormat,
        claimSequences
      );
    }
    return handleError();
  } catch (error) {
    const postError = error as ApiError;
    Logger.error(new Error(postError.message));
    return handleError();
  }
};

// image recommendation

const getImagesReferenceFrameIndex = (data: ImageData) => {
  const { imagesReferenceFrame, frames = [] } = data.imagesRecommend[0];

  return (
    frames.findIndex(frame => frame.id === imagesReferenceFrame) + 1
  ).toString();
};

const getGeneratedImagesFormatted = (
  contentType: string,
  data: ImageData,
  errors: string[]
): GeneratedImageContent => {
  switch (contentType) {
    case ContentType.BANNER:
      return {
        success: true,
        errors,
        data: {
          optionId: data.imagesRecommend[0].optionId,
          imagesReferenceFrame: getImagesReferenceFrameIndex(data),
          images:
            data.imagesRecommend[0]?.frames?.flatMap(frame =>
              frame.recommendedImages
                .filter(image => image.imageName?.trim() !== '')
                .map(image => {
                  const imageObject: {
                    imageId: string;
                    imageName: string;
                    caption?: string;
                    category?: string;
                  } = {
                    imageId: image.imageId,
                    imageName: image.imageName,
                  };

                  if (image.caption) {
                    imageObject.caption = image.caption;
                  }

                  if (image.category) {
                    imageObject.category = image.category;
                  }

                  return imageObject;
                })
            ) ?? [],
        },
      };

    case 'social':
      return {
        success: true,
        errors,
        data: {
          optionId: data.imagesRecommend[0].optionId,
          images:
            data.imagesRecommend[0]?.recommendedImages?.map(item => {
              const imageObject: Image = {
                imageId: item.imageId,
                imageName: item.imageName,
              };

              if (item.caption) {
                imageObject.caption = item.caption;
              }

              if (item.category) {
                imageObject.category = item.category;
              }

              return imageObject;
            }) || [],
        },
      };

    case ContentType.EMAIL:
    case ContentType.RTE.toUpperCase():
      return {
        success: true,
        errors,
        data: {
          optionId: data.imagesRecommend[0].optionId,
          images:
            data.imagesRecommend[0]?.recommendedImages?.map(item => {
              const imageObject: Image = {
                imageId: item.imageId,
                imageName: item.imageName,
              };

              if (item.caption) {
                imageObject.caption = item.caption;
              }

              if (item.category) {
                imageObject.category = item.category;
              }

              return imageObject;
            }) || [],
        },
      };

    default:
      return {
        success: false,
      };
  }
};

type Image = {
  imageId: string;
  imageName: string;
  caption?: string;
  category?: string;
};

export type ImageCaptionData = {
  optionId: string;
  imagesReferenceFrame?: string;
  images: Image[];
};

export type GeneratedImageContent = {
  success: boolean;
  errors?: string[];
  data?: ImageCaptionData;
};

const handleImagesError = (errors: string[]): GeneratedImageContent => {
  const errorResponse = {
    success: false,
    errors,
  };

  return errorResponse;
};

export const fetchRecommendedImages = async (
  formFields: {
    brand: string;
    channel: string;
    contentId: string;
    sessionId: string;
    action: string;
  },
  regenerateSectionDetails: RegenerateSectionDetails
): Promise<GeneratedImageContent> => {
  const payload: HTTPClientRequestPayload = {
    brand: formFields.brand,
    channel: formFields.channel,
    contentId: formFields.contentId,
    sessionId: formFields.sessionId,
    action: formFields.action,
  };

  payload.imageRecommendOptions = [
    {
      optionId: regenerateSectionDetails.optionId,
      fields: regenerateSectionDetails.sections,
    },
  ];

  try {
    const response = await httpClient.post<ImageApiResponse>(
      RECOMMEND_IMAGES,
      payload
    );

    const { data, success, errors } = response as ImageApiResponse;

    if (success && data) {
      return getGeneratedImagesFormatted(payload.channel, data, errors);
    }

    return handleImagesError(errors);
  } catch (error) {
    const postError = error as ApiError;
    Logger.error(new Error(postError.message));
    return handleImagesError([]);
  }
};

export const fetchImagesCaption = async (
  formFields: {
    brand: string;
    channel: string;
    contentId: string;
    sessionId: string;
    action: string;
  },
  regenerateSectionDetails: RegenerateSectionDetails
): Promise<GeneratedImageContent> => {
  const payload: HTTPClientRequestPayload = {
    brand: formFields.brand,
    channel: formFields.channel,
    contentId: formFields.contentId,
    sessionId: formFields.sessionId,
    action: formFields.action,
  };

  payload.assetCaptionOptions = [
    {
      optionId: regenerateSectionDetails.optionId,
      imageIds: regenerateSectionDetails.imageIds,
    },
  ];

  try {
    const response = await httpClient.post<ImageApiResponse>(
      GENERATE_CAPTION,
      payload
    );
    const { data, success, errors } = response as ImageApiResponse;

    if (success && data) {
      return getGeneratedImagesFormatted(payload.channel, data, errors);
    }

    return handleImagesError(errors);
  } catch (error) {
    const postError = error as ApiError;
    Logger.error(new Error(postError.message));
    return handleImagesError([]);
  }
};

type DownloadFileResponseData = {
  success: boolean;
  data?: { content: string; fileName?: string };
};

export interface UploadFileResult {
  success: boolean;
  message: string;
  errors?: string[];
  data: BlobPart;
  headers?: Record<string, string>;
  status: number;
}

export const fetchContentAsset = async (
  imageId: string,
  assetAction: string = '',
  paramName: 'imageId' | 'imageName' = 'imageId'
): Promise<DownloadFileResponseData | ApiError> => {
  try {
    const headers = {
      Accept: 'application/octet-stream',
    };

    const response = (await httpClient.get<UploadFileResult>(
      CONTENT_ASSET(imageId, assetAction, paramName),
      {},
      headers,
      'arraybuffer'
    )) as UploadFileResult;

    const { data, success, headers: responseHeaders } = response;

    if (success && data && responseHeaders) {
      return {
        data: { content: data, fileName: 'imageId' },
        success,
      } as DownloadFileResponseData;
    }
    return { success: false };
  } catch (error) {
    const postError = error as ApiError;
    Logger.error(new Error(postError.message));
    return { success: false };
  }
};

export enum GeneratedContentTypeParam {
  JSON = 'json',
  FILE = 'file',
}

type ParamsXlsxType = {
  sessionId: string;
  action: string;
  contentId: string;
  language?: string;
  type: GeneratedContentTypeParam;
  fileName: string;
  contentGroupId?: string;
  sort?: string[];
  languages?: string;
  allTranslations?: boolean;
};

export const exportGeneratedContent = async (
  params: ParamsXlsxType,
  callback: (isSuccess: boolean) => void
): Promise<DownloadFileResponseData | ApiError> => {
  const {
    sessionId,
    action,
    contentId,
    type,
    contentGroupId,
    sort,
    fileName,
    language,
    languages,
    allTranslations,
  } = params;
  try {
    const url = GET_GENERATED_CONTENT_EXPORT(
      sessionId,
      action,
      contentId,
      type,
      language,
      contentGroupId,
      sort,
      languages,
      allTranslations
    );

    const headers = {
      Accept:
        type === GeneratedContentTypeParam.FILE
          ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          : 'application/json',
    };

    const response = (await httpClient.get<UploadFileResult>(
      url,
      {},
      headers,
      type === GeneratedContentTypeParam.FILE
        ? 'arraybuffer'
        : GeneratedContentTypeParam.JSON
    )) as UploadFileResult;

    let blob: Blob;
    let exportedFileName: string;

    if (type === GeneratedContentTypeParam.FILE) {
      blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });

      exportedFileName = `${fileName}`;
    } else {
      blob = new Blob([JSON.stringify(response.data)], {
        type: 'application/json',
      });

      exportedFileName = `${fileName}.json`;
    }

    // Create a URL for the blob
    const downloadUrl = URL.createObjectURL(blob);

    // Create an anchor element and trigger a download
    const a = document.createElement('a');
    a.href = downloadUrl;
    a.download = exportedFileName;
    document.body.appendChild(a); // Append to the body if necessary
    a.click(); // Programmatically trigger a click event on the anchor element
    document.body.removeChild(a); // Clean up by removing the anchor element

    // Release the object URL to free up resources
    URL.revokeObjectURL(downloadUrl);
    callback(true);
    return { success: true };
  } catch (error) {
    const postError = error as ApiError;
    Logger.error(new Error(postError.message));

    return { success: false };
  }
};

export type ContentConfig = {
  success: boolean;
  data: {
    contentConfig: {
      recommendImages: boolean;
      enableTranslation: boolean;
    };
  };
};

export const fetchContentConfig = async (
  formFields: MarketingFormField
): Promise<GeneratedContent | ContentConfig> => {
  try {
    const response = (await httpClient.get<ContentConfig>(
      CONTENT_CONFIG(
        formFields.brand,
        formFields.targetAudience,
        formFields.contentType,
        formFields.action
      ),
      {}
    )) as ContentConfig;

    const { data, success } = response;

    if (success && data) {
      return { success, data };
    }

    return handleError();
  } catch (error) {
    const getError = error as ApiError;
    Logger.error(new Error(getError.message));
    return handleError();
  }
};

export const fetchWFTaskDetails = async (
  taskId: string,
  projectId: string
): Promise<WfTaskDetailsResponseData> => {
  try {
    const response = await httpClient.get<WfTaskDetailsResult>(
      WF_TASK_DETAILS(taskId, projectId)
    );
    const { success, data } = response as WfTaskDetailsResult;

    if (success && data) {
      return { success, data: getTransformedPromptData(data) };
    }

    return { success: false };
  } catch (error) {
    const postError = error as ApiError;
    Logger.error(new Error(postError.message));
    return { success: false };
  }
};

export const getExtractionDetails = async (
  sessionId: string
): Promise<ExtractionDetailsFuncResp> => {
  try {
    const response = await httpClient.get<ExtractionDetailsResponse>(
      GET_EXTRACTION_DETAILS(sessionId)
    );

    const { data, success } = response as ExtractionDetailsResponse;

    if (success && data) {
      return {
        success: true,
        data,
      };
    }

    return handleError() as unknown as ExtractionDetailsFuncResp;
  } catch (error) {
    const postError = error as ApiError;
    Logger.error(new Error(postError.message));
    return handleError() as unknown as ExtractionDetailsFuncResp;
  }
};

/**
 * Fetches images for a list of recommended image names and returns an array of image data.
 *
 * @param {string[]} recommendedImages - An array of recommended image names.
 * @returns {Promise<ContentImages[]>} A promise that resolves to an array of image data.
 */
const fetchImagesForGeneratedContent = async (
  recommendedImages: string[]
): Promise<ContentImages[]> => {
  const images = await Promise.all(
    recommendedImages.map(async imageName => {
      try {
        const { success, data: contentAssetData } =
          await fetchContentAsset(imageName);

        if (success && contentAssetData) {
          const fileContent = get(contentAssetData, 'content', '');
          const fileExtension = imageName.split('.').pop() || '';
          const blobType = `image/${fileExtension}`;

          try {
            const blob = new Blob([fileContent as BlobPart], {
              type: blobType,
            });

            return {
              imageBlob: blob,
              imageName,
              imageType: fileExtension as string,
            };
          } catch (e) {
            // eslint-disable-next-line no-console
            console.error('Error converting blob to data URL');
            Logger.error(e as Error);
            return null;
          }
        } else {
          // eslint-disable-next-line no-console
          console.error('Error fetching content asset');
          return null;
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Error fetching content asset');
        Logger.error(e as Error);
        return null;
      }
    })
  );

  return images.filter(image => image !== null) as ContentImages[];
};

/**
 * Asynchronously fetches recommended images for each Email Module content item in the provided
 * `GeneratedEmailModuleContentDataType` array, converts image data into blobs, and updates the content
 * data structure with the images data (image blob, image name and image type) for each option.
 *
 * @param {GeneratedEmailModuleContentDataType[]} generatedContent - An array of generated content data objects.
 * Each object contains module content information with potential recommended images.
 * @param {ImagesRecommended[]} imagesRecommended - An array of recommended images for each content item.
 * @param {boolean} [includeImages=false] - A flag indicating whether to include images in the exported file.
 * @returns {Promise<GeneratedEmailModuleContentDataType[]>} - A promise that resolves to a copy of the
 * provided data array, where each content item is updated with the fetched images data.
 */
export const fetchImagesAndUpdateDataForExport = async (
  generatedContent: GeneratedEmailModuleContentDataType[],
  imagesRecommended: ImagesRecommended[] = [],
  includeImages = false
): Promise<GeneratedEmailModuleContentDataType[]> => {
  const transformedEmailModuleContent =
    getTransformedEmailModuleData(generatedContent) || [];

  const updatedEmailModulesContent = await Promise.all(
    transformedEmailModuleContent?.map(async emailContent => {
      const { id, contentOptions = [] } = emailContent || {};

      const contentImagesData = imagesRecommended?.find(
        recommendedImages => recommendedImages?.id === id
      );

      const options = [...contentOptions];

      if (
        contentImagesData &&
        contentImagesData?.contentImages?.length &&
        includeImages
      ) {
        const { contentImages } = contentImagesData;

        await Promise.all(
          contentImages?.map(async contentImage => {
            const { optionId, recommendedImages } = contentImage || {};

            const images =
              await fetchImagesForGeneratedContent(recommendedImages);

            const option = options?.find(opt => opt?.optionId === optionId);

            if (option) {
              option.images = images;
            }
          })
        );
      }

      return {
        ...emailContent,
        contentOptions: options,
      };
    })
  );

  return updatedEmailModulesContent;
};

/**
 * For /generated-content API response, i.e., when the /generated-content API response
 * is getting consumed for Email Modules Export functionality.
 */
export interface ContentResponse extends GeneratedContentResult {
  isNewContent?: boolean;
}

/**
 * For /generated API response, i.e., when the /generated API response
 * is getting consumed for Email Modules Export functionality.
 */
export interface GenerateContentResponse extends GeneratedContent {
  isNewContent?: boolean;
}

// Makes the /generated-content API call
/**
 * Fetches pre-generated content for an Email Module for export functionality by making an API call to the `/generated-content` endpoint.
 *
 * This asynchronous function retrieves the generated content using the module's `parentContentId` and session ID, and updates the translated
 * content with language labels based on the current available languages and translation configuration. It also adds the superScripts data
 * for the primary language content and translated content, if the superScripts data exists.
 *
 * @param {ModuleContent} module - The module object containing details of an Email Module such as `parentContentId` and `translationConfig`
 * (from the `moduleContentsStateSegment` Jotai atom).
 * @param {Option[]} currentAvailableLanguages - An array of available languages, each with `id`, `label` and other properties.
 * @param {boolean} [includeTranslated=false] - A flag indicating whether to include translated content in the exported file.
 * @param {boolean} [isDspConvertFlow=false] - A flag indicating whether the user has generated content through the DSP Upload & Convert flow.
 * @param {boolean} [channelName=''] - Name of the currently selected channel/content type.
 * @returns {Promise<ContentResponse>} A promise that resolves to the `ContentResponse` object containing the fetched and updated content.
 */
export const fetchPregeneratedContentForExport = async (
  module: ModuleContent,
  currentAvailableLanguages: Option[],
  includeTranslated = false,
  isDspConvertFlow = false,
  channelName = ''
): Promise<ContentResponse> => {
  const sessionId = getSessionId();
  const contentSessionId = getContentSessionId();
  const contentGroupId = getContentGroupId();
  const { parentContentId } = module || {};

  const generatedContent: ContentResponse = await getGeneratedContent({
    action: isDspConvertFlow ? CONVERT : GENERATE_CONTENT,
    contentGroupId,
    sessionId,
    contentSessionId,
    contentId: parentContentId,
    channel: channelName,
  });

  generatedContent.isNewContent = false;

  const { data, success } = generatedContent || {};

  if (data && success && generatedContent?.data) {
    generatedContent.data.primaryMessage = module.primaryMessage;

    if (module && Object.keys(module)?.length > 0 && includeTranslated) {
      const { translationConfig } = module || {};

      const sortedContentsArray = sortContentsArrayForExport(
        generatedContent?.data
          ?.generatedContent as GeneratedEmailModuleContentDataType[]
      );

      const translatedContentsArray = generatedContent?.data
        ?.translatedContent as GeneratedEmailModuleContentDataType[];

      let updatedTranslatedContentsArray: TranslatedContent[] = [];

      const claimSequences = sequenceAndStoreClaims(
        data as unknown as ContentGeneratedResult['data']
      );

      generatedContent.data.generatedContent =
        generatedContent?.data?.generatedContent?.map(content => {
          return {
            ...content,
            superScripts:
              getTransformedEmailModulesSuperScripts(
                content?.superScripts as EmailModulesSuperScriptsType[],
                claimSequences
              ) || [],
          };
        }) as GeneratedEmailModuleContentDataType[];

      if (
        translationConfig &&
        Object.keys(translationConfig)?.length > 0 &&
        generatedContent?.data?.translatedContent &&
        generatedContent?.data?.translatedContent?.length > 0
      ) {
        updatedTranslatedContentsArray =
          addTranslatedLanguageLabelForSelectedModule(
            sortedContentsArray,
            translatedContentsArray,
            translationConfig,
            currentAvailableLanguages
          );

        generatedContent.data.translatedContent =
          updatedTranslatedContentsArray;

        generatedContent.data.translatedContent =
          generatedContent?.data?.translatedContent?.map(content => {
            return {
              ...content,
              superScripts:
                getTransformedEmailModulesSuperScripts(
                  content?.superScripts as EmailModulesSuperScriptsType[],
                  claimSequences
                ) || [],
            };
          }) as GeneratedEmailModuleContentDataType[];
      }
    }

    return generatedContent as ContentResponse;
  }

  throw new Error(
    `Error fetching the generated content for ${
      module?.label && module?.label?.trim() !== '' ? `${module?.label} ` : ''
    }Email Module for export.`
  );
};

// Makes the /generate API call
/**
 * Generates content for an Email Module, for export functionality, by making an API call to the `/generate` endpoint.
 *
 * This asynchronous function constructs a payload using the data filled in the Marketing form and claim details,
 * then makes an API call to the `/generate` endpoint to fetch the generated content. If successful, it returns
 * the content along with a success flag and marks it as new content.
 *
 * @param {ParamsUsed} paramsUsed - An object containing the data filled in the Marketing form, used to generate the content.
 * @param {PregenerateClaimsFeild} claimsDetail - An object containing claims details required for content generation.
 * @returns {Promise<GenerateContentResponse>} A promise that resolves to a `GenerateContentResponse` object containing the generated content and success status.
 *
 * @throws Will throw an error if the content generation fails.
 */
export const generateContentForExport = async (
  paramsUsed: ParamsUsed,
  claimsDetail: PregenerateClaimsFeild
): Promise<GenerateContentResponse> => {
  const generatePayload = getGenerateContentPayload(paramsUsed);

  const payload = {
    ...generatePayload,
    ...claimsDetail,
  };

  const { data, success } = await fetchGeneratedContent(payload);

  if (success) {
    return {
      data,
      success,
      isNewContent: true,
      primaryMessage: paramsUsed.currentSelectedTopic[0],
    };
  }

  throw new Error(
    `Error generating content for ${
      payload &&
      payload?.emailModules &&
      typeof payload?.emailModules === 'string'
        ? `${payload?.emailModules} `
        : ''
    }Email Module for export.`
  );
};

// =======================================================
// All Email Modules export functionality code BEGINS here
// =======================================================

// Will decide whether to call /generate or /generated-content API for extracting the content for Export functionality
/**
 * Determines whether to call the `/generate` or `/generated-content` API to extract content for Email Modules for export functionality.
 *
 * This asynchronous function processes an array of module contents and decides which API to call based on the presence
 * of `parentContentId`. It uses pre-fetched claims and parameter values to customize the content generation process.
 * The function returns a promise that resolves to an array of results, indicating the success or failure of each
 * Email Module content generation or content fetching operation.
 *
 * @param {ModuleContent[]} modulesContent - An array of module content objects to be processed for export.
 * @param {Claim[]} preFetchedclaimsList - A list of claims that have been pre-fetched and may influence content generation.
 * @param {ParamsUsed} paramsSegmentValue - Parameters used to customize the content generation process.
 * @param {Option[]} currentAvailableLanguages - An array of available languages, each with `id`, `label` and other properties.
 * @param {boolean} [includeTranslated=false] - A flag indicating whether to include translated content in the exported file.
 * @param {boolean} [isDspConvertFlow=false] - A flag indicating whether the user has generated content through the DSP Upload & Convert flow.
 * @param {boolean} [isRTEFlow=false] - A flag indicating whether the user has generated content through the RTE Generate or DSP Upload & Convert to RTE flow.
 * @returns {Promise<PromiseSettledResult<GenerateContentResponse | ContentResponse>[]>} A promise that resolves to an array of settled results, each representing the outcome of a content generation or fetching operation.
 *
 * @example
 * const modules = [{ name: 'Module1', parentContentId: '123' }, { name: 'Module2' }];
 * const claims = [{ id: 'claim1', isSubstitute: false }];
 * const params = { someParam: 'value' };
 * const languages = [{ id: 'en', label: 'English' }];
 * generateContentForAllModules(modules, claims, params, languages).then(results => {
 *   results.forEach(result => {
 *     if (result.status === 'fulfilled') {
 *       console.log('Content fetched or generated successfully:', result.value);
 *     } else {
 *       console.error('Error fetching or generating content:', result.reason);
 *     }
 *   });
 * });
 */
export const generateContentForAllModules = async (
  modulesContent: ModuleContent[],
  preFetchedclaimsList: Claim[],
  paramsSegmentValue: ParamsUsed,
  currentAvailableLanguages: Option[],
  includeTranslated = false,
  isDspConvertFlow = false,
  isRTEFlow = false
): Promise<
  PromiseSettledResult<GenerateContentResponse | ContentResponse>[]
> => {
  // Map over each module content to create a promise for content generation or fetching
  const contentPromises = modulesContent?.map(module => {
    const { parentContentId, name: moduleName, primaryMessage } = module || {};

    // If `parentContentId` exists for the Email Module, fetch pre-generated content
    if (parentContentId && parentContentId?.trim() !== '') {
      let channelName = '';

      if (import.meta.env.MODE === 'development') {
        channelName = isDspConvertFlow
          ? paramsSegmentValue?.currentSelectedDestinationFormat || ''
          : paramsSegmentValue?.currentSelectedContentType || '';
      }

      return fetchPregeneratedContentForExport(
        module,
        currentAvailableLanguages,
        includeTranslated,
        isDspConvertFlow,
        channelName
      );
    }

    const shouldUseSubstitute = preFetchedclaimsList?.some(
      el => el.isSubstitute
    );

    const claimsFields = {
      sessionId: getSessionId(),
      useSubstituteClaims: shouldUseSubstitute,
      claims: getPrefetchedClaimListIds(
        isRTEFlow && primaryMessage.length
          ? [primaryMessage]
          : paramsSegmentValue.currentSelectedTopic
      ),
    };

    const paramsValue: ParamsUsed = {
      ...paramsSegmentValue,
      currentSelectedModules: moduleName,
    };

    if (isRTEFlow && primaryMessage.length) {
      paramsValue.currentSelectedTopic = [primaryMessage];
    }

    return generateContentForExport(
      paramsValue,
      claimsFields as unknown as PregenerateClaimsFeild
    );
  });

  // Return a promise that resolves when all content promises have settled
  return Promise.allSettled(contentPromises);
};

// for All Modules
/**
 * This asynchronous function processes an array of module contents. If new content has been generated for an Email Module,
 * it updates the value of the `moduleContentsStateSegment` Jotai atom, and also adds the superScripts data (if it exists).
 * Else, if pre-generated content has been fetched for an Email Module, it fetches and adds each recommended image's data
 * for each option (if any recommended image(s) exist for the Email Module).
 *
 * @param {ModuleContent[]} modulesContent - An array of module content objects to be processed for state updates.
 * @param {Claim[]} preFetchedclaimsList - A list of claims that have been pre-fetched and may influence content generation.
 * @param {ParamsUsed} paramsSegmentValue - Parameters used to customize the content generation process.
 * @param {Map<string, ModuleContent>} emailModuleContents - A map of current email module contents keyed by a string identifier.
 * @param {SetAtom<SetStateActionWithReset<Map<string, ModuleContent>>, void>} setEmailModuleContents - A setter function to update the state of email module contents.
 * @param {Option[]} currentAvailableLanguages - An array of available languages, each with `id`, `label` and other properties.
 * @param {boolean} [includeImages=false] - A flag indicating whether to include images in the exported file.
 * @param {boolean} [includeTranslated=false] - A flag indicating whether to include translated content in the exported file.
 * @param {boolean} [isDspConvertFlow=false] - A flag indicating whether the user has generated content through the DSP Upload & Convert flow.
 * @param {boolean} [isRTEFlow=false] - A flag indicating whether the user has generated content through the RTE Generate or DSP Upload & Convert to RTE flow.
 * @returns {Promise<PromiseSettledResult<GenerateContentResponse | ContentResponse>[]>} A promise that resolves to an array of settled results, each representing the outcome of a content generation or fetching operation.
 */
export const updateModuleContentsStateSegment = async (
  modulesContent: ModuleContent[],
  preFetchedclaimsList: Claim[],
  paramsSegmentValue: ParamsUsed,
  emailModuleContents: Map<string, ModuleContent>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setEmailModuleContents: (args_0: any) => void,
  currentAvailableLanguages: Option[],
  includeImages = false,
  includeTranslated = false,
  isDspConvertFlow = false,
  isRTEFlow = false
): Promise<
  PromiseSettledResult<GenerateContentResponse | ContentResponse>[]
> => {
  type ExtendedGeneratePayload = MarketingFormFieldRequest &
    PregenerateClaimsFeild;

  function processModuleContents(
    modules: ModuleContent[]
  ): ExtendedGeneratePayload | null {
    let firstGeneratePayload: ExtendedGeneratePayload | null = null;

    const names = modules?.reduce<string[]>((acc, module) => {
      if (!firstGeneratePayload && module?.generatePayload) {
        // Use type assertion to include emailModules
        firstGeneratePayload = {
          ...module?.generatePayload,
          emailModules: '',
        } as ExtendedGeneratePayload;
      }

      acc.push(module.name);
      return acc;
    }, []);

    const emailModules = names.join(', ');

    if (firstGeneratePayload) {
      (firstGeneratePayload as ExtendedGeneratePayload).emailModules =
        emailModules;
    }

    return firstGeneratePayload;
  }

  const payloadForTracking = processModuleContents(modulesContent);

  // track generate form initate event
  trackGenerateContentInitiate(ANALYTICS.GENERATE_FORM_NAME);
  // track the generate content submit here
  // track submit value here

  if (payloadForTracking) {
    const { brand, contentType } = payloadForTracking;

    trackGenerateContentSubmit(
      ANALYTICS.GENERATE_FORM_NAME,
      brand,
      contentType,
      getEncodedUserNtid()
    );
  }

  try {
    // Generate new content or fetch pre-generated content for all modules
    const responses = await generateContentForAllModules(
      modulesContent,
      preFetchedclaimsList,
      paramsSegmentValue,
      currentAvailableLanguages,
      includeTranslated,
      isDspConvertFlow,
      isRTEFlow
    );

    // Create a new map to store updated module contents
    const updatedModuleContentsStateSegment = new Map(emailModuleContents);

    await Promise.all(
      // Promise.all to ensure we await all promises
      responses?.map(async (response, index) => {
        if (response?.status === 'fulfilled') {
          const {
            value: { isNewContent },
          } = response;

          if (isNewContent) {
            // Handle new content generated by the `/generate` API call
            const module = modulesContent?.[index];
            if (
              !module?.parentContentId ||
              module?.parentContentId?.trim() === ''
            ) {
              const updatedModule: ModuleContent = {
                ...module,
                parentContentId:
                  (response?.value as GenerateContentResponse)?.data?.id ?? '',
                hasContentGenerated: true,
                status: 'In Progress',
              };

              updatedModuleContentsStateSegment.set(
                module?.mapKey,
                updatedModule
              );
            }

            (
              (response.value as GenerateContentResponse).data
                ?.content as GeneratedEmailModuleContentDataType
            ).superScripts =
              (response.value as GenerateContentResponse).data?.superScripts ||
              [];
          } else {
            // Handle existing content from the `/generated-content` API call
            const { data } = (response?.value as ContentResponse) || {};

            if (data && data?.generatedContent) {
              const transformedImagesData: ImagesRecommended[] =
                data?.generatedContent?.map(content => ({
                  id: content?.id,
                  parentId: content?.parentId,
                  versionSequence: content?.versionSequence,
                  contentImages:
                    content?.imagesRecommend?.map(imageRec => ({
                      optionId: imageRec?.optionId,
                      recommendedImages:
                        imageRec?.recommendedImages?.map(
                          img => img?.imageName
                        ) || [],
                    })) || [],
                }));

              data.generatedContent = await fetchImagesAndUpdateDataForExport(
                data?.generatedContent as GeneratedEmailModuleContentDataType[],
                transformedImagesData,
                includeImages
              );

              data.translatedContent = await fetchImagesAndUpdateDataForExport(
                data?.translatedContent as GeneratedEmailModuleContentDataType[],
                transformedImagesData,
                includeImages
              );
            }
          }
        } else if (response.status === 'rejected') {
          // If content fetching/generation fails for any module, then what should
          // go in the Word document for that module - that needs to come here.
          // eslint-disable-next-line no-console
          console.error(
            `Error updating module contents for index ${index}:`,
            response.reason
          );
        }
      })
    );

    if (payloadForTracking) {
      const {
        useSubstituteClaims,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        verbalIdentity,
        ...restPayload
      } = payloadForTracking;
      const { currentSelectedLanguage } = store.get(contentParamsSegment);
      const generateInputValues = {
        ...restPayload,
        'secondary-claims': useSubstituteClaims ? ANALYTICS.YES : ANALYTICS.NO,
        language: currentSelectedLanguage,
        [ANALYTICS.USER_ENCODED_NTID_QUESTION_NAME]: getEncodedUserNtid(),
      };

      // track input values here against the generate form
      trackInputValues(
        ANALYTICS.GENERATE_FORM_NAME,
        ANALYTICS.STEP,
        generateInputValues
      );

      // track content complete
      trackFormComplete(ANALYTICS.GENERATE_FORM_NAME);
    }

    // Update the state of email module contents
    setEmailModuleContents(updatedModuleContentsStateSegment);

    return responses;
  } catch (error) {
    // track generate content error here
    trackFormError(
      ANALYTICS.GENERATE_FORM_NAME,
      'failed to load all modules content'
    );
    // eslint-disable-next-line no-console
    console.error('Error updating module contents:', error);
    return [];
  }
};

// =====================================================
// All Email Modules export functionality code ENDS here
// =====================================================

// =========================================================
// Single Email Module export functionality code BEGINS here
// =========================================================

/**
 * Determines whether to call the `/generate` or `/generated-content` API to extract content for a single Email Module for export functionality.
 *
 * This function checks if a `parentContentId` is provided to determine the appropriate API call. If a valid `parentContentId`
 * is present, it fetches pre-generated content by triggering the `/generated-content` API. Otherwise, it generates new content
 * by invoking the `/generate` API, using specified parameters and claims. The function also considers available languages to
 * ensure content is fetched or generated in the desired language.
 *
 * @param {ModuleContent} selectedModuleContent - The module content object containing details such as `parentContentId` and `name`.
 * @param {Claim[]} preFetchedclaimsList - A list of claims that have been pre-fetched and may influence content generation.
 * @param {ParamsUsed} paramsSegmentValue - Parameters used to customize the content generation process.
 * @param {Option[]} currentAvailableLanguages - An array of available languages, each with `id`, `label` and other properties.
 * @param {boolean} [includeTranslated=false] - A flag indicating whether to include translated content in the exported file.
 * @param {boolean} [isDspConvertFlow=false] - A flag indicating whether the user has generated content through the DSP Upload & Convert flow.
 * @param {boolean} [isRTEFlow=false] - A flag indicating whether the user has generated content through the RTE Generate or DSP Upload & Convert to RTE flow.
 * @returns {Promise<GenerateContentResponse | ContentResponse>} A promise that resolves to either a `GenerateContentResponse` or `ContentResponse` object, depending on the API call made.
 *
 * @example
 * const moduleContent = { parentContentId: '12345', name: 'Module1' };
 * const claims = [{ id: 'claim1', isSubstitute: false }];
 * const params = { someParam: 'value' };
 * const languages = [{ id: 'en', label: 'English' }];
 * getSingleModuleContents(moduleContent, claims, params, languages).then(response => {
 *   console.log('Content retrieved:', response);
 * });
 */
export const getSingleModuleContents = (
  selectedModuleContent: ModuleContent,
  preFetchedclaimsList: Claim[],
  paramsSegmentValue: ParamsUsed,
  currentAvailableLanguages: Option[],
  includeTranslated = false,
  isDspConvertFlow = false,
  isRTEFlow = false
): Promise<GenerateContentResponse | ContentResponse> => {
  const {
    parentContentId,
    name: moduleName,
    primaryMessage,
  } = selectedModuleContent || {};
  // Check if a valid parentContentId is provided
  if (parentContentId && parentContentId?.trim() !== '') {
    // Fetch pre-generated content by triggering the `/generated-content` API
    let channelName = '';

    if (import.meta.env.MODE === 'development') {
      channelName = isDspConvertFlow
        ? paramsSegmentValue?.currentSelectedDestinationFormat || ''
        : paramsSegmentValue?.currentSelectedContentType || '';
    }

    return fetchPregeneratedContentForExport(
      selectedModuleContent,
      currentAvailableLanguages,
      includeTranslated,
      isDspConvertFlow,
      channelName
    );
  }

  const shouldUseSubstitute = preFetchedclaimsList?.some(el => el.isSubstitute);

  const claimsFields = {
    sessionId: getSessionId(),
    useSubstituteClaims: shouldUseSubstitute,
    claims: getPrefetchedClaimListIds(
      isRTEFlow && primaryMessage.length
        ? [primaryMessage]
        : paramsSegmentValue.currentSelectedTopic
    ),
  };

  const paramsValue: ParamsUsed = {
    ...paramsSegmentValue,
    currentSelectedModules: moduleName,
  };

  if (isRTEFlow && primaryMessage.length) {
    paramsValue.currentSelectedTopic = [primaryMessage];
  }

  return generateContentForExport(
    paramsValue,
    claimsFields as unknown as PregenerateClaimsFeild
  );
};

// for Single Module
/**
 * This asynchronous function processes the contents of an Email Module, and updates the value of the `moduleContentsStateSegment` Jotai atom
 * if new content has been generated for that particular Email Module, or optionally fetches and adds each recommended image's data for each option,
 * if pre-generated content has been fetched for that particular Email Module.
 *
 * @param {ModuleContent} selectedModuleContent - The module content object to be processed for state updates.
 * @param {Claim[]} preFetchedclaimsList - A list of claims that have been pre-fetched and may influence content generation.
 * @param {ParamsUsed} paramsSegmentValue - Parameters used to customize the content generation process.
 * @param {Map<string, ModuleContent>} emailModuleContents - A map of current email module contents keyed by a string identifier.
 * @param {SetAtom<SetStateActionWithReset<Map<string, ModuleContent>>, void>} setEmailModuleContents - A Jotai function to update the state of email module contents.
 * @param {Option[]} currentAvailableLanguages - An array of available languages, each with `id`, `label` and other properties.
 * @param {boolean} [isCurrentModule=false] - A flag indicating whether the current module is being processed, affecting image fetching behavior.
 * @param {boolean} [includeImages=false] - A flag indicating whether to include images in the exported file.
 * @param {boolean} [includeTranslated=false] - A flag indicating whether to include translated content in the exported file.
 * @param {boolean} [isDspConvertFlow=false] - A flag indicating whether the user has generated content through the DSP Upload & Convert flow.
 * @param {boolean} [isRTEFlow=false] - A flag indicating whether the user has generated content through the RTE Generate or DSP Upload & Convert to RTE flow.
 * @returns {Promise<GenerateContentResponse | ContentResponse>} A promise that resolves to either a `GenerateContentResponse` or `ContentResponse` object, depending on the API call made.
 */
export const updateModuleContentsStateSegmentForSingleModule = async (
  selectedModuleContent: ModuleContent,
  preFetchedclaimsList: Claim[],
  paramsSegmentValue: ParamsUsed,
  emailModuleContents: Map<string, ModuleContent>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setEmailModuleContents: (args_0: any) => void,
  currentAvailableLanguages: Option[],
  isCurrentModule = false,
  includeImages = false,
  includeTranslated = false,
  isDspConvertFlow = false,
  isRTEFlow = false
): Promise<GenerateContentResponse | ContentResponse> => {
  function getFirstGeneratePayload(
    modules: Map<string, ModuleContent>
  ): (MarketingFormFieldRequest & PregenerateClaimsFeild) | null {
    // Convert map values to an array and use find to locate the first non-null generatePayload
    const moduleContentArray = Array.from(modules.values());
    const foundModule = moduleContentArray.find(
      moduleContent => moduleContent.generatePayload !== null
    );

    return foundModule ? foundModule.generatePayload : null;
  }

  try {
    const response = await getSingleModuleContents(
      selectedModuleContent,
      preFetchedclaimsList,
      paramsSegmentValue,
      currentAvailableLanguages,
      includeTranslated,
      isDspConvertFlow,
      isRTEFlow
    );

    const updatedModuleContentsStateSegment = new Map(emailModuleContents);

    const { isNewContent } = response;

    if (isNewContent) {
      // track generate form initate event
      trackGenerateContentInitiate(ANALYTICS.GENERATE_FORM_NAME);

      const payloadForTracking = getFirstGeneratePayload(emailModuleContents);

      // track the generate content submit here
      // track submit value here
      if (payloadForTracking) {
        const { brand, contentType } = payloadForTracking;
        const { currentSelectedLanguage } = store.get(contentParamsSegment);

        trackGenerateContentSubmit(
          ANALYTICS.GENERATE_FORM_NAME,
          brand,
          contentType,
          getEncodedUserNtid()
        );

        const {
          useSubstituteClaims,
          destinationFormat,
          emailModules,
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          verbalIdentity,
          ...restPayload
        } = payloadForTracking;

        const shouldTrackEmailModules =
          contentType?.toLowerCase() === ContentType.EMAIL.toLowerCase() ||
          destinationFormat?.toLowerCase() === ContentType.EMAIL.toLowerCase();

        const generateInputValues = {
          ...restPayload,
          'secondary-claims': useSubstituteClaims
            ? ANALYTICS.YES
            : ANALYTICS.NO,
          ...(shouldTrackEmailModules && { emailModules }),
          language: currentSelectedLanguage,
          emailModules: selectedModuleContent?.name, // email module which was selected
          [ANALYTICS.USER_ENCODED_NTID_QUESTION_NAME]: getEncodedUserNtid(),
        };

        // track input values here against the generate form
        trackInputValues(
          ANALYTICS.GENERATE_FORM_NAME,
          ANALYTICS.STEP,
          generateInputValues
        );

        // track content complete
        trackFormComplete(ANALYTICS.GENERATE_FORM_NAME);
      }

      // for /generate API call
      const module = selectedModuleContent;

      if (!module?.parentContentId || module?.parentContentId?.trim() === '') {
        const updatedModule: ModuleContent = {
          ...module,
          parentContentId:
            (response?.data as GeneratedContentDataType)?.id ?? '',
          hasContentGenerated: true,
          status: 'In Progress',
        };

        updatedModuleContentsStateSegment.set(module.mapKey, updatedModule);
      }
    } else {
      // for /generated-content API call
      const { data } = (response as GeneratedContentResult) || {};

      if (data && data?.generatedContent && !isCurrentModule) {
        const transformedImagesData: ImagesRecommended[] =
          data?.generatedContent?.map(content => ({
            id: content?.id,
            parentId: content?.parentId,
            versionSequence: content?.versionSequence,
            contentImages:
              content?.imagesRecommend?.map(imageRec => ({
                optionId: imageRec?.optionId,
                recommendedImages:
                  imageRec?.recommendedImages?.map(img => img.imageName) || [],
              })) || [],
          }));

        data.generatedContent = await fetchImagesAndUpdateDataForExport(
          data?.generatedContent as GeneratedEmailModuleContentDataType[],
          transformedImagesData,
          includeImages
        );

        data.translatedContent = await fetchImagesAndUpdateDataForExport(
          data?.translatedContent as GeneratedEmailModuleContentDataType[],
          transformedImagesData,
          includeImages
        );
      }
    }

    setEmailModuleContents(updatedModuleContentsStateSegment);
    return response;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error updating module contents:', error);
    return {} as GenerateContentResponse | ContentResponse;
  }
};

// =======================================================
// Single Email Module export functionality code ENDS here
// =======================================================

export type SupportingInformation = {
  success: boolean;
  data: {
    supportingInfo: {
      prescribingInfo: Array<{
        id: string;
        label: string;
        link: string;
      }>;
      safetyInfo: {
        id: string;
        text: string;
      };
    };
  };
};

export const fetchSupportingInformation = async ({
  brandName,
  countryName,
  indicationName,
  audienceName,
}: {
  brandName: string;
  countryName: string;
  indicationName?: string;
  audienceName: string;
}) => {
  try {
    const response = (await httpClient.get<SupportingInformation>(
      SUPPORTING_INFORMATION,
      {
        brandName,
        countryName,
        indicationName,
        audienceName,
      }
    )) as SupportingInformation;
    const { data, success } = response;

    if (success && data) {
      return { success, data } as SupportingInformation;
    }

    return handleError();
  } catch (error) {
    const getError = error as ApiError;
    Logger.error(new Error(getError.message));
    return handleError();
  }
};

export const fetchGeneratedContentValidation = async ({
  contentId,
  sessionId,
  contentGroupId,
  action,
}: {
  contentId: string;
  sessionId: string;
  contentGroupId: string;
  action: string;
}) => {
  try {
    const response = (await httpClient.post<ValiditionApiResponse>(
      GENERATED_CONTENT_VALIDATION,
      {
        contentId,
        sessionId,
        contentGroupId,
        action,
      }
    )) as ValiditionApiResponse;
    const { data, success } = response;

    if (success && data) {
      return { success, data } as ValiditionApiResponse;
    }

    return handleError();
  } catch (error) {
    const getError = error as ApiError;
    Logger.error(new Error(getError.message));
    return handleError();
  }
};
// type VersionType = {
//   version?: string;
//   date?: string;
// };

export const fetchVersionInfo = async (): Promise<VersionAPIData | null> => {
  try {
    // prettier-ignore
    const response = await httpClient.get<APIVersionInfoResponse>(GET_VERSION_INFO);
    const { data } = response as APIVersionInfoResponse;
    return data || null;
  } catch (error) {
    Logger.error(
      new Error(
        get(error, 'response.data.message', 'Error fetching information')
      )
    );

    return null;
  }
};

export const saveSessionContext = async (
  requestPayload: SessionContextPayload,
  method: 'post' | 'patch'
) => {
  try {
    const response = (await httpClient[method]<SaveSessionContextApiResponse>(
      SAVE_USER_SESSION_CONTEXT(requestPayload.sessionId),
      requestPayload
    )) as SaveSessionContextApiResponse;
    const { data, success } = response;

    if (success && data) {
      return { success, data } as SaveSessionContextApiResponse;
    }

    return handleError();
  } catch (error) {
    const getError = error as ApiError;
    Logger.error(new Error(getError.message));
    return handleError();
  }
};

export interface GetGeneratedContentPreviewParams {
  sessionId: string;
  action: string;
  contentId: string;
  contentGroupId?: string;
  channel?: string;
  contentSessionId?: string;
  requestedContentsData?: string;
}

export interface GeneratedContentItem {
  id: string;
  parentId?: string;
  versionSequence?: number;
  language?: string;
  operationType?: string;
  name?: string;
  attributes?: Array<{
    id: string;
    name: string;
    code: string;
    sequence: number;
  }>;
  contentOptions?: Array<{
    optionId: string;
    header?: string;
    preHeader?: string;
    body?: string;
    closingBody?: string;
    clickToAction?: string;
    subheader?: string;
    bulletedCopy?: string[];
    subjectLine?: string;
    previewText?: string;
  }>;
  superScripts?: Array<{
    optionId: string;
    superScriptReferences?: Record<string, string[]>;
  }>;
  imagesRecommend?: Array<{
    optionId: string;
    recommendedImages?: Array<{
      imageId: string;
      imageName: string;
      caption?: string;
    }>;
  }>;
  contentReferences?: Array<{
    id: string;
    code?: string;
    description?: string;
    documentNames?: string[];
    referenceLink?: string;
    status?: string;
    isSubstitute?: boolean;
    category?: string;
  }>;
  requestParams?: Record<string, unknown>;
  score?: {
    value: number;
  };
}

export interface GeneratedContentPreviewResult {
  success: boolean;
  data?: {
    sessionId?: string;
    contentSessionId?: string;
    channel?: string;
    generatedContent?: GeneratedContentItem[];
    translatedContent?: GeneratedContentItem[];
    selectedOption?: string; // Keep for backward compatibility
    selectedOptionSuperScripts?: unknown[]; // Keep for backward compatibility
  };
  errors?: string[];
  message?: string;
}

export const getGeneratedContentPreview = async ({
  sessionId,
  action,
  contentId,
  contentGroupId,
  channel,
  contentSessionId,
  requestedContentsData,
}: GetGeneratedContentPreviewParams): Promise<GeneratedContentPreviewResult> => {
  try {
    const response = await httpClient.get<GeneratedContentPreviewResult>(
      GET_GENERATED_CONTENT_PREVIEW(
        sessionId,
        action,
        contentId,
        contentGroupId,
        channel,
        contentSessionId,
        requestedContentsData
      )
    );

    const { data, success } = response as GeneratedContentPreviewResult;
    if (success && data) {
      return {
        success,
        data,
      };
    }
    return handleError() as GeneratedContentPreviewResult;
  } catch (error) {
    const apiError = error as ApiError;
    Logger.error(new Error(apiError.message));
    return handleError() as GeneratedContentPreviewResult;
  }
};

/**
 * Updates generated content with new attribute values for a specific option.
 *
 * @param params - The request parameters for updating content.
 * @returns A promise that resolves to the update result.
 */
export const updateGeneratedContent = async ({
  sessionId,
  contentSessionId,
  contentGroupId,
  action,
  channel,
  contentId,
  optionId,
  updatedAttributes,
  contentType = 'email', // Default to email, can be 'banner', 'social', 'email', 'dsp', 'claims', 'modules'
  frameId,
}: {
  sessionId: string;
  contentSessionId: string;
  contentGroupId: string;
  action: string;
  channel: string;
  contentId: string;
  optionId: string;
  updatedAttributes: Record<string, string | string[]>;
  contentType?: string;
  frameId?: string;
}): Promise<UpdateGeneratedContentResponse> => {
  try {
    // Construct the content option based on content type
    const contentOption: {
      optionId: string;
      [key: string]: unknown;
    } = { optionId };

    // Map the updated attributes to the correct structure based on content type
    const frames: Array<{ id: string; body: string }> = [];

    Object.entries(updatedAttributes).forEach(([key, value]) => {
      if (contentType.toLowerCase() === ContentType.BANNER.toLowerCase()) {
        // For banner content with frames
        if (key.startsWith('frame_') && key.endsWith('_body')) {
          // Use the actual frameId passed from the component instead of constructing it
          const actualFrameId = frameId || `frame${key.split('_')[1]}`;

          // Collect all frame updates
          frames.push({
            id: actualFrameId,
            body: value as string,
          });
        } else if (key === 'call_to_action') {
          contentOption.callToAction = value as string;
        }
      } else if (
        contentType.toLowerCase() === ContentType.SOCIAL.toLowerCase()
      ) {
        // For social content with shortForm/longForm
        if (key === 'shortFormHeadline') {
          if (!contentOption.shortForm) {
            contentOption.shortForm = {};
          }
          (contentOption.shortForm as Record<string, unknown>).headline =
            value as string;
        } else if (key === 'shortFormBody') {
          if (!contentOption.shortForm) {
            contentOption.shortForm = {};
          }
          (contentOption.shortForm as Record<string, unknown>).body =
            value as string;
        } else if (key === 'longFormHeadline') {
          if (!contentOption.longForm) {
            contentOption.longForm = {};
          }
          (contentOption.longForm as Record<string, unknown>).headline =
            value as string;
        } else if (key === 'longFormBody') {
          if (!contentOption.longForm) {
            contentOption.longForm = {};
          }
          (contentOption.longForm as Record<string, unknown>).body =
            value as string;
        } else if (key === 'shortFormHashtags') {
          if (!contentOption.shortForm) {
            contentOption.shortForm = {};
          }
          // Convert hashtags text to array format as expected by the API
          const hashtagsArray = (value as string)
            .split(',')
            .filter(tag => tag.trim());
          (contentOption.shortForm as Record<string, unknown>).hashtags =
            hashtagsArray;
        } else if (key === 'longFormHashtags') {
          if (!contentOption.longForm) {
            contentOption.longForm = {};
          }
          // Convert hashtags text to array format as expected by the API
          const hashtagsArray = (value as string)
            .split(',')
            .filter(tag => tag.trim());
          (contentOption.longForm as Record<string, unknown>).hashtags =
            hashtagsArray;
        }
      } else if (
        contentType.toLowerCase() === ContentType.UPLOAD.toLowerCase()
      ) {
        // For DSP content - handle summary text updates
        if (key === 'summaryText') {
          // This will be handled in the main payload construction below
          // Store the summary update info
          contentOption.summaryUpdate = {
            summaryText: value as string,
          };
        }
      } else {
        // For email/module content - direct mapping
        contentOption[key] = value;
      }
    });

    // Assign frames array if any frames were updated (for banner content)
    if (frames.length > 0) {
      contentOption.frames = frames;
    }

    // Construct the payload based on content type
    let generatedContentUpdate: GeneratedContentUpdateItem;

    if (contentType.toLowerCase() === ContentType.UPLOAD.toLowerCase()) {
      // For DSP content, use contentSummary structure
      generatedContentUpdate = {
        id: contentId,
        contentSummary: {
          summaries: [
            {
              id: optionId,
              summaryText:
                (contentOption.summaryUpdate as { summaryText: string })
                  ?.summaryText || '',
            },
          ],
        },
      };
    } else if (
      contentType.toLowerCase() === ContentType.CORE_MESSAGE.toLowerCase()
    ) {
      // For core message content, use updateComponents structure
      const updateComponentData: {
        header?: string;
        subheader?: string;
        message?: string;
        callToAction?: string;
      } = {};

      // Map the updated attributes to the correct component data fields
      Object.entries(updatedAttributes).forEach(([key, value]) => {
        if (key === 'header') {
          updateComponentData.header = value as string;
        } else if (key === 'subheader') {
          updateComponentData.subheader = value as string;
        } else if (key === 'message') {
          updateComponentData.message = value as string;
        } else if (key === 'callToAction') {
          updateComponentData.callToAction = value as string;
        }
      });

      generatedContentUpdate = {
        id: contentId,
        updateComponents: [
          {
            id: optionId,
            updateComponentData,
          },
        ],
      };
    } else {
      // For other content types, use contentOptions structure
      generatedContentUpdate = {
        id: contentId,
        contentOptions: [contentOption],
      };
    }

    const requestPayload: UpdateGeneratedContentRequest = {
      data: {
        sessionId,
        contentSessionId,
        contentGroupId,
        action,
        channel,
        generatedContentUpdate: [generatedContentUpdate],
      },
    };

    const response = await httpClient.patch<UpdateGeneratedContentResponse>(
      UPDATE_GENERATED_CONTENT,
      requestPayload
    );

    const { data, success, message, errors } =
      response as UpdateGeneratedContentResponse;

    if (success && data) {
      return {
        success,
        data,
        message,
      };
    }

    return {
      success: false,
      message: message || 'Failed to update generated content',
      errors: errors || [],
    };
  } catch (error) {
    const apiError = error as ApiError;
    Logger.error(new Error(apiError.message));

    return {
      success: false,
      message: apiError.message || 'An error occurred while updating content',
      errors: [apiError.message || 'Unknown error'],
    };
  }
};
